/**
 * Hooks TanStack Query — œuvres + séries + tags + vision_jobs + media URLs.
 *
 * Tous les appels serveur passent par TanStack Query. Pas de `useState` +
 * `useEffect` + `axios.get` — c'est une convention CLAUDE.md verrouillée
 * (`'fetch(' direct interdit en ESLint`, le pattern correct est documenté).
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'

import { mediaApi } from '@/utils/api/media'
import { tagsApi, type TagListParams } from '@/utils/api/tags'
import { seriesApi, worksApi, type WorkListParams } from '@/utils/api/works'
import type {
  BulkVariantCreateRequest,
  SeriesCreateRequest,
  SeriesResponse,
  SeriesWorkSummary,
  TagResponse,
  VisionJobResponse,
  WorkCreateRequest,
  WorkDeliverableResponse,
  WorkDigitalFormatsPreviewResponse,
  WorkListResponse,
  WorkResponse,
  WorkUpdateRequest,
  WorkVariantInput,
  WorkVariantUpdateRequest,
  WorkVariantWithCatalogResponse,
} from '@/types/works'

// ──────────────────────────────────────────────────────────────────────────────
// Query keys — point d'entrée unique pour invalidation cohérente
// ──────────────────────────────────────────────────────────────────────────────

export const worksKeys = {
  all: ['works'] as const,
  lists: () => [...worksKeys.all, 'list'] as const,
  list: (filters: WorkListParams) => [...worksKeys.lists(), filters] as const,
  detail: (id: number) => [...worksKeys.all, 'detail', id] as const,
  visionJob: (id: number) => [...worksKeys.all, 'vision-job', id] as const,
  digitalFormatsPreview: (id: number) => [...worksKeys.all, 'digital-formats-preview', id] as const,
}

export const tagsKeys = {
  all: ['tags'] as const,
  list: (params: TagListParams) => [...tagsKeys.all, 'list', params] as const,
}

export const seriesKeys = {
  all: ['series'] as const,
  list: () => [...seriesKeys.all, 'list'] as const,
  works: (seriesId: number) => [...seriesKeys.all, 'works', seriesId] as const,
}

export const mediaKeys = {
  all: ['media'] as const,
  url: (minioKey: string) => [...mediaKeys.all, 'url', minioKey] as const,
}

// ──────────────────────────────────────────────────────────────────────────────
// Works
// ──────────────────────────────────────────────────────────────────────────────

export function useWorks(filters: WorkListParams) {
  return useQuery<WorkListResponse>({
    queryKey: worksKeys.list(filters),
    queryFn: () => worksApi.list(filters),
  })
}

export function useWork(id: number | null | undefined) {
  return useQuery<WorkResponse>({
    queryKey: worksKeys.detail(id ?? -1),
    queryFn: () => worksApi.get(id as number),
    enabled: id !== null && id !== undefined && id > 0,
  })
}

/** Preview live des livrables digitaux résolus dynamiquement (refonte
 *  2026-05-14). Consommé par <VariantModal> en mode digital pour afficher
 *  l'encart « X fichiers seront livrés : A3 (1:√2), 50×70 (5:7), … ».
 *  Activé uniquement quand `enabled=true` (= modal ouvert en mode digital). */
export function useDigitalFormatsPreview(
  workId: number | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<WorkDigitalFormatsPreviewResponse>({
    queryKey: worksKeys.digitalFormatsPreview(workId ?? -1),
    queryFn: () => worksApi.getDigitalFormatsPreview(workId as number),
    enabled: enabled && workId !== null && workId !== undefined && workId > 0,
  })
}

export function useCreateWork() {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, WorkCreateRequest>({
    mutationFn: (payload) => worksApi.create(payload),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      qc.setQueryData(worksKeys.detail(created.id), created)
    },
  })
}

export function useAddMediaToWork(id: number) {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, File[]>({
    mutationFn: (files) => worksApi.addMedia(id, files),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      void qc.invalidateQueries({ queryKey: worksKeys.visionJob(id) })
    },
  })
}

export function useSetCanonicalSource(id: number) {
  // Migration 015 — bascule le source canonical d'une œuvre. Met à jour
  // optimistiquement le cache détail + invalide la grille publique
  // (`thumb_minio_key` suit le canonical via JOIN BE).
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, number>({
    mutationFn: (mediaId) => worksApi.setCanonicalSource(id, mediaId),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

export function useDeleteMedia(id: number) {
  // Hard-delete d'un source (ratio) — typiquement un upload raté. Le BE
  // refuse si dernier source ou si canonical avec d'autres sources. Le
  // cache détail est rafraîchi avec la réponse (carrousel ratios), la
  // grille publique invalidée.
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, number>({
    mutationFn: (mediaId) => worksApi.deleteMedia(id, mediaId),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

export function useUpdateWork(id: number) {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, WorkUpdateRequest>({
    mutationFn: (payload) => worksApi.update(id, payload),
    onSuccess: (updated, variables) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      // Si l'œuvre a changé de série (ou de position via auto-assign), les
      // listes managées du panneau de réordonnancement deviennent stale.
      // Invalider à la racine `seriesKeys.all` couvre l'ancienne ET la
      // nouvelle série sans devoir tracker prev_series_id côté caller.
      if ('series_id' in variables) {
        void qc.invalidateQueries({ queryKey: seriesKeys.all })
      }
    },
  })
}

export function useArchiveWork(id: number) {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, void>({
    mutationFn: () => worksApi.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

export function useUnarchiveWork(id: number) {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, void>({
    mutationFn: () => worksApi.unarchive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

/** Met l'œuvre en ligne sur le portfolio (status='published'). 422 si
 *  les seuils éditoriaux FR ne sont pas atteints. */
export function usePublishWorkToPortfolio(id: number) {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, void>({
    mutationFn: () => worksApi.publishToPortfolio(id),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

/** Retire l'œuvre du portfolio (status='draft', `published_at` historique conservé). */
export function useUnpublishWorkFromPortfolio(id: number) {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, void>({
    mutationFn: () => worksApi.unpublishFromPortfolio(id),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

export function useDuplicateWork() {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, number>({
    mutationFn: (id) => worksApi.duplicate(id),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      qc.setQueryData(worksKeys.detail(created.id), created)
    },
  })
}

export function useAttachTags(id: number) {
  const qc = useQueryClient()
  return useMutation<WorkResponse, { detail: string; status?: number }, number[]>({
    mutationFn: (tagIds) => worksApi.attachTags(id, tagIds),
    onSuccess: (updated) => {
      qc.setQueryData(worksKeys.detail(id), updated)
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      void qc.invalidateQueries({ queryKey: tagsKeys.all })
    },
  })
}

export function useDetachTag(id: number) {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (tagId) => worksApi.detachTag(id, tagId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
      void qc.invalidateQueries({ queryKey: tagsKeys.all })
    },
  })
}

export function useDeleteWork() {
  const qc = useQueryClient()
  return useMutation<void, { detail: string; status?: number }, number>({
    mutationFn: (id) => worksApi.remove(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: worksKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Vision job — polling 3s tant que pending/running
// ──────────────────────────────────────────────────────────────────────────────

export function useVisionJob(workId: number | null | undefined) {
  return useQuery<VisionJobResponse | null>({
    queryKey: worksKeys.visionJob(workId ?? -1),
    queryFn: () => worksApi.getVisionJob(workId as number),
    enabled: workId !== null && workId !== undefined && workId > 0,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      // Continue le polling tant que le job n'est pas terminal.
      return data.status === 'pending' || data.status === 'running' ? 3000 : false
    },
  })
}

export function useRetryVision(workId: number) {
  const qc = useQueryClient()
  return useMutation<VisionJobResponse, { detail: string; status?: number }, void>({
    mutationFn: () => worksApi.retryVision(workId),
    onSuccess: (job) => {
      qc.setQueryData(worksKeys.visionJob(workId), job)
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Tags
// ──────────────────────────────────────────────────────────────────────────────

export function useTags(params: TagListParams = {}) {
  return useQuery<TagResponse[]>({
    queryKey: tagsKeys.list(params),
    queryFn: () => tagsApi.list(params),
    staleTime: 60_000,
  })
}

export function useCreateTag() {
  const qc = useQueryClient()
  return useMutation<
    TagResponse,
    { detail: string; status?: number },
    { label_fr: string; kind?: TagResponse['kind'] }
  >({
    mutationFn: (payload) =>
      tagsApi.create({ label_fr: payload.label_fr, kind: payload.kind ?? 'theme' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tagsKeys.all })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Variantes (S2.5b → S2.5d : 1:N formats)
// ──────────────────────────────────────────────────────────────────────────────

export function useAddVariant() {
  const qc = useQueryClient()
  return useMutation<
    WorkVariantWithCatalogResponse,
    { detail: string; status?: number },
    { workId: number; payload: WorkVariantInput }
  >({
    mutationFn: ({ workId, payload }) => worksApi.addVariant(workId, payload),
    onSuccess: (_data, { workId }) => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
    },
  })
}

/**
 * Crée N variantes en transaction tout-ou-rien (refonte POD v6.1).
 * Consommé par `<VariantsBulkCreateForm>` (PR-C wizard 5 étapes, à venir).
 * La route BE valide chaque payload AVANT d'ouvrir la transaction —
 * un payload invalide rollback toute la batch.
 */
export function useBulkAddVariants() {
  const qc = useQueryClient()
  return useMutation<
    WorkVariantWithCatalogResponse[],
    { detail: string; status?: number },
    { workId: number; payload: BulkVariantCreateRequest }
  >({
    mutationFn: ({ workId, payload }) => worksApi.bulkAddVariants(workId, payload),
    onSuccess: (_data, { workId }) => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
    },
  })
}

export function useUpdateVariant() {
  const qc = useQueryClient()
  return useMutation<
    WorkVariantWithCatalogResponse,
    { detail: string; status?: number },
    { workId: number; variantId: number; payload: WorkVariantUpdateRequest }
  >({
    mutationFn: ({ workId, variantId, payload }) =>
      worksApi.updateVariant(workId, variantId, payload),
    onSuccess: (_data, { workId }) => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
    },
  })
}

export function useDeleteVariant() {
  const qc = useQueryClient()
  return useMutation<
    void,
    { detail: string; status?: number },
    { workId: number; variantId: number }
  >({
    mutationFn: ({ workId, variantId }) => worksApi.deleteVariant(workId, variantId),
    onSuccess: (_data, { workId }) => {
      void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Deliverables digitaux (S2.5d) — JPG q100 + PDF générés par variante
// ──────────────────────────────────────────────────────────────────────────────

export function useVariantDeliverables(workId: number, variantId: number, enabled = true) {
  return useQuery<WorkDeliverableResponse[]>({
    queryKey: ['works', 'detail', workId, 'variants', variantId, 'deliverables'],
    queryFn: () => worksApi.listDeliverables(workId, variantId),
    enabled,
    staleTime: 60_000,
  })
}

export function useRegenerateDeliverables() {
  const qc = useQueryClient()
  return useMutation<
    WorkDeliverableResponse[],
    { detail: string; status?: number },
    { workId: number; variantId: number }
  >({
    mutationFn: ({ workId, variantId }) => worksApi.regenerateDeliverables(workId, variantId),
    onSuccess: (_data, { workId, variantId }) => {
      void qc.invalidateQueries({
        queryKey: ['works', 'detail', workId, 'variants', variantId, 'deliverables'],
      })
      void qc.invalidateQueries({ queryKey: worksKeys.detail(workId) })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Séries
// ──────────────────────────────────────────────────────────────────────────────

export function useSeriesList(
  options?: Omit<UseQueryOptions<SeriesResponse[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SeriesResponse[]>({
    queryKey: seriesKeys.list(),
    queryFn: () => seriesApi.list(),
    staleTime: 5 * 60_000,
    ...options,
  })
}

export function useCreateSeries() {
  const qc = useQueryClient()
  return useMutation<SeriesResponse, { detail: string; status?: number }, SeriesCreateRequest>({
    mutationFn: (payload) => seriesApi.create(payload),
    onSuccess: (created) => {
      qc.setQueryData<SeriesResponse[]>(seriesKeys.list(), (prev) => {
        if (!prev) return [created]
        if (prev.some((s) => s.id === created.id)) return prev
        return [...prev, created]
      })
    },
  })
}

export function useSeriesWorks(
  seriesId: number | null | undefined,
  options?: Omit<UseQueryOptions<SeriesWorkSummary[]>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<SeriesWorkSummary[]>({
    queryKey: seriesId ? seriesKeys.works(seriesId) : ['series', 'works', 'disabled'],
    queryFn: () => seriesApi.listWorks(seriesId as number),
    enabled: seriesId !== null && seriesId !== undefined && seriesId > 0,
    staleTime: 60_000,
    ...options,
  })
}

export function useReorderSeries(seriesId: number) {
  const qc = useQueryClient()
  return useMutation<
    SeriesWorkSummary[],
    { detail: string; status?: number },
    { ordered_work_ids: number[] }
  >({
    mutationFn: (payload) => seriesApi.reorder(seriesId, payload),
    onSuccess: (members) => {
      qc.setQueryData(seriesKeys.works(seriesId), members)
      // Les œuvres ont vu leur `series_position` muter — invalider les
      // détails individuels pour que `useWork(id)` resynchronise.
      for (const m of members) {
        void qc.invalidateQueries({ queryKey: worksKeys.detail(m.id) })
      }
      void qc.invalidateQueries({ queryKey: worksKeys.lists() })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Media — URL signée
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Récupère l'URL signée pour rendre `<img src>`. Cache staleTime 4 min
 * (TTL signature MinIO = 5 min), donc l'image n'expire jamais en plein
 * affichage. `enabled=false` si la clé est null/undefined (états de
 * chargement avant que les media de l'œuvre soient connus).
 */
export function useMediaUrl(minioKey: string | null | undefined) {
  return useQuery<string>({
    queryKey: mediaKeys.url(minioKey ?? ''),
    queryFn: async () => {
      const res = await mediaApi.getUrl(minioKey as string)
      return res.url
    },
    enabled: !!minioKey,
    staleTime: 4 * 60_000,
    gcTime: 10 * 60_000,
  })
}
